import { useCallback, useMemo } from 'react'
import { type AreaData, type AreaId, isDefined, Logger } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { searchAccountsKey } from '../search/keys'
import { areaCountKey, areaGetKey, areaListInfiniteKey, areaCanvassKey } from '../keys'
import { applyAreaChangeToV2Caches, findCachedArea, scheduleAreaListRefresh } from './cacheSync'

const logger = new Logger('useAreaCache')

type Result = {
  clearList: VoidFunction
  clearSelf: VoidFunction
  clearArea: (areaId: AreaId) => void
  clearSearchAccounts: VoidFunction
  addArea: (area: AreaData) => void
  updateArea: (area: AreaData) => void
  removeArea: (areaId: AreaId, options?: { archive?: boolean }) => void
}

export function useAreaCache(): Result {
  const queryClient = useQueryClient()

  const clearList = useCallback((): void => {
    logger.verbose('invalidateQueries areaListInfiniteKey')
    queryClient.invalidateQueries({ queryKey: areaListInfiniteKey() })
    queryClient.invalidateQueries({ queryKey: areaCountKey() })
  }, [])

  const clearSelf = useCallback((): void => {
    logger.verbose('invalidateQueries areaCanvassKey')
    queryClient.invalidateQueries({ queryKey: areaCanvassKey() })
  }, [])

  const clearArea = useCallback((areaId: AreaId): void => {
    logger.verbose('invalidateQueries areaGetKey', areaId)
    queryClient.invalidateQueries({ queryKey: areaGetKey(areaId) })
  }, [])

  const clearSearchAccounts = useCallback((): void => {
    logger.verbose('invalidateQueries searchAccountsKey')
    queryClient.invalidateQueries({ queryKey: searchAccountsKey() })
  }, [])

  const addArea = useCallback((area: AreaData): void => {
    logger.verbose('updating cache', area)
    applyAreaChangeToV2Caches(queryClient, { areaId: area.areaId, before: 'new', after: area })
    scheduleAreaListRefresh(queryClient)
    clearSelf()
  }, [])

  const updateArea = useCallback((area: AreaData): void => {
    logger.verbose('updating cache', area)
    const before = findCachedArea(queryClient, area.areaId)
    queryClient.setQueryData<AreaData>(areaGetKey(area.areaId), area)
    applyAreaChangeToV2Caches(queryClient, { areaId: area.areaId, before, after: area })
    scheduleAreaListRefresh(queryClient)
    clearSelf()
  }, [])

  /** `archive` (default true) trashes the area, so trash lists can pick it up; false means a hard delete. */
  const removeArea = useCallback((areaId: AreaId, { archive = true }: { archive?: boolean } = {}): void => {
    logger.verbose('removing areaId from cache', areaId)
    const before = findCachedArea(queryClient, areaId)
    const after = archive && isDefined(before) ? { ...before, isDeleted: true } : undefined
    applyAreaChangeToV2Caches(queryClient, { areaId, before, after })
    scheduleAreaListRefresh(queryClient)
    clearSelf()
  }, [])

  return useMemo(
    () => ({
      clearList,
      clearSelf,
      clearArea,
      clearSearchAccounts,
      addArea,
      updateArea,
      removeArea,
    }),
    [clearList, clearSelf, clearArea, clearSearchAccounts, addArea, updateArea, removeArea]
  )
}
